import styled from 'styled-components';

export type ChatSummary = {
  id: string;
  title: string;
  lastMessage: string;
};

export type Message = {
  id: string;
  chatId: string;
  text: string;
  from: 'me' | 'them';
  createdAt: string;
  isRead: boolean;
};

export const ChatWrapper = styled.div<{ $isSmallScreen?: boolean }>`
  position: fixed;
  right: 2.4rem;
  bottom: 2.4rem;
  z-index: 999;

  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1.2rem;

  ${({ $isSmallScreen }) =>
    $isSmallScreen &&
    `
    margin-right: -4.4rem;
    transform: translateY(-6rem);
  `}

  @media print {
    display: none;
  }
`;

export const ChatPanel = styled.div<{ $isFullscreen?: boolean }>`
  width: 34rem;
  max-width: 100vw;
  height: 46rem;
  max-height: calc(var(--vh, 1vh) * 80);

  background-color: var(--white);
  border-radius: 0.8rem;
  border: 0.1rem solid var(--gray-50);
  box-shadow: 0 0.8rem 2.4rem rgba(0, 0, 0, 0.25);

  display: grid;
  grid-template-rows: auto 1fr auto;
  overflow: hidden;

  ${({ $isFullscreen }) =>
    $isFullscreen &&
    `
    position: fixed;
    inset: 0;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: calc(var(--vh, 1vh) * 100);
    max-width: none;
    max-height: none;
    border-radius: 0;
    z-index: 999999;
  `}
`;

export const ChatHeader = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  align-items: center;
  gap: 0.8rem;

  padding: 1.2rem 1.6rem;
  border-bottom: 0.1rem solid var(--gray-50);
  background-color: var(--gray-30);
`;

export const ChatHeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const ChatHeaderMainTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

export const ChatBody = styled.div`
  position: relative;
  padding: 1.2rem 1.2rem 0.8rem;
  background-color: var(--gray-30);
  overflow: hidden;
`;

export const ChatFooter = styled.form<{ $hasMessageText: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-columns: max-content;
  grid-auto-flow: column;
  gap: 0.8rem;

  padding: 0.8rem 1.2rem 1.2rem;
  border-top: 0.1rem solid var(--gray-50);
  background-color: var(--gray-30);

  ${({ $hasMessageText }) =>
    $hasMessageText &&
    `
    & #chat-select-all {
      display: none;
    }
  `}
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: rgba(0, 0, 0, 0.1);
  z-index: 1;
`;

export const ChatItemGap = styled.div<{ isLast: boolean }>`
  padding-bottom: ${({ isLast }) => (isLast ? '0' : '0.4rem')};
`;

export const MessageContent = styled.div<{ isMine?: boolean }>`
  max-width: 80%;
  display: flex;
  flex-direction: column;
  align-items: ${({ isMine }) => (isMine ? 'flex-end' : 'flex-start')};
`;

export const StyledVirtuosoWrapper = styled.div`
  height: 100%;
  width: 100%;

  & > * {
    width: calc(100% + 1.2rem);

    & > * {
      padding-right: 1.2rem;
    }
  }
`;

export const ChatItem = styled.div<{ isActive: boolean; isOdd: boolean }>`
  box-sizing: border-box;
  width: 100%;

  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.8rem;
  align-items: center;

  padding: 0.8rem;
  border-radius: 0.6rem;
  background-color: ${({ isActive, isOdd }) =>
    isActive ? 'var(--primary-30)' : isOdd ? 'var(--gray-50)' : 'transparent'};

  &:hover {
    background-color: ${({ isActive }) => (isActive ? 'var(--primary-40)' : 'var(--gray-50)')};
  }
`;

export const ChatItemContent = styled.div`
  cursor: pointer;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.2rem;
  min-width: 0;
`;

export const ChatItemCheckboxWrapper = styled.div`
  margin-left: 0.2rem;
`;

export const ChatItemBottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  margin-top: 0.2rem;
`;

export const MessagesContainer = styled.div`
  height: 100%;
`;

export const MessageRow = styled.div<{ alignRight: boolean }>`
  display: flex;
  justify-content: ${({ alignRight }) => (alignRight ? 'flex-end' : 'flex-start')};
  padding: 0.2rem 0;
`;

export const MessageBubble = styled.div<{ isMine: boolean }>`
  padding: 0.6rem 0.8rem;
  border-radius: 1.2rem;
  font-size: var(--font-size-2);
  line-height: var(--line-height-2);
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;

  background-color: ${({ isMine }) => (isMine ? 'var(--primary-100)' : 'var(--gray-80)')};
  color: ${({ isMine }) => (isMine ? 'var(--white)' : 'var(--white)')};
`;

export const MessageTime = styled.div<{ isMine?: boolean }>`
  margin-top: 0.2rem;
  font-size: 1.1rem;
  color: var(--gray-60);
  text-align: ${({ isMine }) => (isMine ? 'right' : 'left')};
`;

export const EmptyState = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.6rem;
  text-align: center;
`;
